import { ActivationCertificateVerifier, type PublicKeyResolver } from './ActivationCertificateVerifier';
import type { ActivationClaims } from './ActivationClaims';
import { verifyServerToken } from './ServerTokenVerifier';

export const OFFLINE_FILE_FORMAT = 'nexxus-licence-file';
export const OFFLINE_FILE_VERSION = 1;
/** Taille maximale acceptée (un fichier légitime fait moins de 4 Ko). */
export const OFFLINE_FILE_MAX_CHARS = 64 * 1024;
/** Tolérance d'horloge entre le poste et le serveur. */
const CLOCK_TOLERANCE_SEC = 300;

export type OfflineFileErrorCode =
  | 'FICHIER_ILLISIBLE'
  | 'FORMAT_INCONNU'
  | 'SIGNATURE_INVALIDE'
  | 'AUTRE_POSTE'
  | 'EXPIRE'
  | 'FICHIER_PERIME'
  | 'NON_ACTIF'
  | 'INCOHERENT'
  | 'OFFRE_CLOUD'
  | 'HORLOGE';

export const OFFLINE_FILE_MESSAGES: Record<OfflineFileErrorCode, string> = {
  FICHIER_ILLISIBLE: 'Ce fichier n’est pas un fichier de licence Nexxus lisible.',
  FORMAT_INCONNU: 'Ce fichier n’est pas un fichier de licence Nexxus (format ou version inconnus).',
  SIGNATURE_INVALIDE:
    'La signature du fichier est invalide : il a été modifié ou ne provient pas de Nexxus. Demandez un nouveau fichier.',
  AUTRE_POSTE:
    'Ce fichier a été préparé pour un autre poste. Demandez à Nexxus un fichier avec l’empreinte de CE poste.',
  EXPIRE: 'La licence contenue dans ce fichier est expirée. Renouvelez la licence puis demandez un nouveau fichier.',
  FICHIER_PERIME:
    'Ce fichier est trop ancien (valable 30 jours après son export). Demandez un nouveau fichier de licence.',
  NON_ACTIF:
    'La licence n’est pas active (suspendue, révoquée ou expirée) : le fichier est refusé. Contactez Nexxus.',
  INCOHERENT: 'Les éléments du fichier ne correspondent pas entre eux : fichier refusé.',
  OFFRE_CLOUD: 'L’offre Cloud fonctionne dans le navigateur : elle ne s’importe pas sur un poste.',
  HORLOGE:
    'L’horloge de ce poste semble décalée : vérifiez la date et l’heure de Windows, puis recommencez.',
};

export type OfflineFileVerification =
  | { ok: true; claims: ActivationClaims; certificate: string; statusIat: number }
  | { ok: false; code: OfflineFileErrorCode; message: string };

function fail(code: OfflineFileErrorCode): OfflineFileVerification {
  return { ok: false, code, message: OFFLINE_FILE_MESSAGES[code] };
}

interface ParsedFile {
  certificate: string;
  status_jwt: string;
  tenant_id: string;
}

function parseFile(raw: string): ParsedFile | OfflineFileErrorCode {
  if (typeof raw !== 'string' || raw.length === 0 || raw.length > OFFLINE_FILE_MAX_CHARS) {
    return 'FICHIER_ILLISIBLE';
  }
  let data: unknown;
  try {
    data = JSON.parse(raw.replace(/^﻿/, ''));
  } catch {
    return 'FICHIER_ILLISIBLE';
  }
  if (typeof data !== 'object' || data === null || Array.isArray(data)) return 'FICHIER_ILLISIBLE';
  const f = data as Record<string, unknown>;
  if (f.format !== OFFLINE_FILE_FORMAT || f.version !== OFFLINE_FILE_VERSION) return 'FORMAT_INCONNU';
  if (
    typeof f.certificate !== 'string' ||
    typeof f.status_jwt !== 'string' ||
    typeof f.tenant_id !== 'string' ||
    f.certificate.split('.').length !== 3 ||
    f.status_jwt.split('.').length !== 3
  ) {
    return 'FORMAT_INCONNU';
  }
  return { certificate: f.certificate, status_jwt: f.status_jwt, tenant_id: f.tenant_id };
}

/**
 * Vérifie un fichier de licence hors ligne SANS rien écrire : signature ES256 du certificat ET du statut
 * (clé publique embarquée), liaison à l'empreinte de CE poste, non-expiration, statut « actif »
 * à l'export (preuve de non-révocation, valable 30 jours), cohérence licence/statut/client.
 * Aucune valeur n'est lue d'un jeton non vérifié.
 */
export async function verifyOfflineLicenceFile(
  raw: string,
  options: {
    deviceFingerprint: string;
    nowSec?: number;
    keyResolver?: PublicKeyResolver;
  },
): Promise<OfflineFileVerification> {
  const parsed = parseFile(raw);
  if (typeof parsed === 'string') return fail(parsed);
  const nowSec = options.nowSec ?? Math.floor(Date.now() / 1000);

  const verifier = new ActivationCertificateVerifier(undefined, options.keyResolver);
  const state = await verifier.verifyCertificate(
    parsed.certificate,
    options.deviceFingerprint,
    '',
    0,
    nowSec,
  );
  if (!state.claims) {
    return fail(state.errorCode === 'NOT_YET_VALID' ? 'HORLOGE' : 'SIGNATURE_INVALIDE');
  }
  if (state.errorCode === 'DEVICE_MISMATCH') return fail('AUTRE_POSTE');
  if (state.errorCode === 'NOT_YET_VALID') return fail('HORLOGE');
  if (state.errorCode === 'INVALID_SIGNATURE') return fail('SIGNATURE_INVALIDE');
  if (state.errorCode === 'EXPIRED' || state.claims.exp <= nowSec) return fail('EXPIRE');
  if (!state.valid) return fail('SIGNATURE_INVALIDE');
  const claims = state.claims;
  if (claims.data_mode === 'REMOTE_MULTI_STATION' || claims.deployment_mode === 'cloud') {
    return fail('OFFRE_CLOUD');
  }

  const status = await verifyServerToken(parsed.status_jwt, 'status', {
    deviceFingerprint: options.deviceFingerprint,
    nowSec,
    keyResolver: options.keyResolver,
  });
  if (!status.ok) {
    if (status.reason === 'ERR_JWT_EXPIRED') return fail('FICHIER_PERIME');
    if (status.reason === 'autre_poste') return fail('AUTRE_POSTE');
    return fail('SIGNATURE_INVALIDE');
  }
  const p = status.payload;
  if (p.status !== 'active') return fail('NON_ACTIF');
  // Le statut doit viser la MÊME licence et le MÊME client que le certificat (pas de collage de pièces).
  if (p.tenant_id !== claims.tenant_id || p.sub !== claims.sub || parsed.tenant_id !== claims.tenant_id) {
    return fail('INCOHERENT');
  }
  const iat = typeof p.iat === 'number' ? p.iat : 0;
  if (iat > nowSec + CLOCK_TOLERANCE_SEC) return fail('HORLOGE');

  return { ok: true, claims, certificate: parsed.certificate, statusIat: iat };
}
